import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import {
  LogOut, Search, Filter, Download, CheckCircle2, XCircle, Trash2,
  Users, Calendar as CalIcon, Loader2, RefreshCw, Phone, Mail as MailIcon,
  ShoppingBag, ChefHat, ClipboardList, Bell, BellOff
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "drago_admin_token";

const R_STATUS = {
  confirmed: { label: "Confermata", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  pending:   { label: "In attesa",  cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  cancelled: { label: "Annullata",  cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

const O_STATUS = {
  new:        { label: "Nuovo",      cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  preparing:  { label: "In prep.",   cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  ready:      { label: "Pronto",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  delivered:  { label: "Consegnato", cls: "bg-white/10 text-white/60 border-white/20" },
  cancelled:  { label: "Annullato",  cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("orders"); // orders | reservations

  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rStats, setRStats] = useState({ total: 0, today: 0, upcoming: 0, cancelled: 0 });
  const [oStats, setOStats] = useState({ total: 0, new: 0, preparing: 0, delivered: 0 });

  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fetching, setFetching] = useState(false);
  const [actioning, setActioning] = useState(null);

  // --- Sound notifications ---
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("drago_admin_sound") !== "off");
  const lastCountsRef = useRef({ ordersNew: null, reservationsToday: null });
  const audioCtxRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("drago_admin_sound", soundOn ? "on" : "off");
  }, [soundOn]);

  const playChime = useCallback(() => {
    if (!soundOn) return;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [{ f: 659.25, t: 0 }, { f: 880.00, t: 0.18 }].forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.25, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.55);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.6);
      });
    } catch (e) { console.warn("Audio chime blocked:", e); }
  }, [soundOn]);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  const fetchAll = useCallback(async () => {
    setFetching(true);
    try {
      if (tab === "reservations") {
        const params = {};
        if (dateFilter) params.date = dateFilter;
        if (statusFilter && statusFilter !== "all") params.status = statusFilter;
        if (q.trim()) params.q = q.trim();
        const [list, stats] = await Promise.all([
          axios.get(`${API}/reservations`, { params, headers: headers() }),
          axios.get(`${API}/reservations/stats`, { headers: headers() }),
        ]);
        setReservations(list.data || []);
        setRStats(stats.data);
        // sound notify on new reservation today
        const todayCount = stats.data?.today ?? 0;
        if (lastCountsRef.current.reservationsToday !== null && todayCount > lastCountsRef.current.reservationsToday) {
          playChime();
          toast.success("Nuova prenotazione ricevuta.");
        }
        lastCountsRef.current.reservationsToday = todayCount;
      } else {
        const params = {};
        if (statusFilter && statusFilter !== "all") params.status = statusFilter;
        if (q.trim()) params.q = q.trim();
        const [list, stats] = await Promise.all([
          axios.get(`${API}/orders`, { params, headers: headers() }),
          axios.get(`${API}/orders/stats`, { headers: headers() }),
        ]);
        setOrders(list.data || []);
        setOStats(stats.data);
        // sound notify on new order
        const newCount = stats.data?.new ?? 0;
        if (lastCountsRef.current.ordersNew !== null && newCount > lastCountsRef.current.ordersNew) {
          playChime();
          toast.success("Nuovo ordine ricevuto.");
        }
        lastCountsRef.current.ordersNew = newCount;
      }
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      toast.error("Errore caricamento.");
    } finally {
      setFetching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dateFilter, statusFilter, q]);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  // Auto-poll every 30s per notifiche in tempo reale
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(() => {
      fetchAll();
    }, 30000);
    return () => clearInterval(iv);
  }, [user, fetchAll]);

  useEffect(() => {
    // reset filters when switching tab
    setStatusFilter("all");
    setDateFilter("");
    setQ("");
  }, [tab]);

  if (loading) {
    return <div className="min-h-screen bg-ink grid place-items-center text-white/60"><Loader2 className="animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateRStatus = async (id, status) => {
    setActioning(id + status);
    try {
      await axios.patch(`${API}/reservations/${id}`, { status }, { headers: headers() });
      toast.success("Aggiornata.");
      fetchAll();
    } catch { toast.error("Errore."); }
    finally { setActioning(null); }
  };

  const updateOStatus = async (id, status) => {
    setActioning(id + status);
    try {
      await axios.patch(`${API}/orders/${id}`, { status }, { headers: headers() });
      toast.success("Aggiornato.");
      fetchAll();
    } catch { toast.error("Errore."); }
    finally { setActioning(null); }
  };

  const removeRes = async (id) => {
    if (!window.confirm("Eliminare la prenotazione?")) return;
    setActioning(id + "del");
    try {
      await axios.delete(`${API}/reservations/${id}`, { headers: headers() });
      toast.success("Eliminata.");
      fetchAll();
    } catch { toast.error("Errore."); }
    finally { setActioning(null); }
  };

  const removeOrd = async (id) => {
    if (!window.confirm("Eliminare l'ordine?")) return;
    setActioning(id + "del");
    try {
      await axios.delete(`${API}/orders/${id}`, { headers: headers() });
      toast.success("Eliminato.");
      fetchAll();
    } catch { toast.error("Errore."); }
    finally { setActioning(null); }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/reservations/export.csv`, { headers: headers(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `drago-ramen-reservations-${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV scaricato.");
    } catch { toast.error("Errore export."); }
  };

  const resetFilters = () => { setQ(""); setDateFilter(""); setStatusFilter("all"); };

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size={34} showText={false} />
            <div>
              <div className="font-display text-sm">Drago <span className="text-gold">Ramen</span></div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Dashboard Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSoundOn((v) => !v)}
              data-testid="admin-sound-toggle"
              title={soundOn ? "Notifiche sonore attive" : "Notifiche sonore mute"}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                soundOn ? "text-gold border-gold/40 hover:bg-gold/10" : "text-white/50 border-white/10 hover:border-white/30"
              }`}
            >
              {soundOn ? <Bell size={13} /> : <BellOff size={13} />}
              <span className="hidden sm:inline">{soundOn ? "Suoni ON" : "Suoni OFF"}</span>
            </button>
            <a href="/" className="text-xs text-white/60 hover:text-white transition-colors hidden sm:inline" data-testid="admin-view-site">Vedi sito ↗</a>
            <span className="text-xs text-white/50 hidden md:inline" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full transition-colors">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">Il tuo comando</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight" data-testid="admin-title">
              Ordini & <span className="text-ember">Prenotazioni</span>.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 border border-white/10 hover:border-white/30 text-white/80 px-4 py-2.5 rounded-full text-sm transition-colors">
              <RefreshCw size={14} className={fetching ? "animate-spin" : ""} /> Aggiorna
            </button>
            {tab === "reservations" && (
              <button onClick={exportCSV} data-testid="admin-export-csv-btn" className="inline-flex items-center gap-2 bg-gold/90 hover:bg-gold text-ink px-5 py-2.5 rounded-full text-sm font-medium transition-colors">
                <Download size={14} /> Esporta CSV
              </button>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="inline-flex mb-8 rounded-full bg-ash/60 border border-white/10 p-1" data-testid="admin-tabs">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={<ShoppingBag size={14} />} testId="admin-tab-orders">Ordini</TabBtn>
          <TabBtn active={tab === "reservations"} onClick={() => setTab("reservations")} icon={<CalIcon size={14} />} testId="admin-tab-reservations">Prenotazioni</TabBtn>
        </div>

        {tab !== "qr" && tab === "orders" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-orders">
            <StatCard icon={<ClipboardList size={16} />} label="Totali" value={oStats.total} />
            <StatCard icon={<ShoppingBag size={16} />} label="Nuovi" value={oStats.new} accent />
            <StatCard icon={<ChefHat size={16} />} label="In prep." value={oStats.preparing} />
            <StatCard icon={<CheckCircle2 size={16} />} label="Consegnati" value={oStats.delivered} />
          </div>
        )}
        {tab !== "qr" && tab === "reservations" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={<CalIcon size={16} />} label="Totali" value={rStats.total} />
            <StatCard icon={<Users size={16} />} label="Oggi" value={rStats.today} accent />
            <StatCard icon={<CheckCircle2 size={16} />} label="Prossime" value={rStats.upcoming} />
            <StatCard icon={<XCircle size={16} />} label="Annullate" value={rStats.cancelled} />
          </div>
        )}

        {/* Filters */}
        {tab !== "qr" && (
        <div className={`rounded-2xl bg-ash/60 backdrop-blur-md border border-white/10 p-4 md:p-5 mb-6 grid grid-cols-1 gap-3 ${tab === "reservations" ? "md:grid-cols-[1fr_180px_180px_auto]" : "md:grid-cols-[1fr_180px_auto]"}`}>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono..." data-testid="admin-search-input" className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold transition-colors" />
          </div>
          {tab === "reservations" && (
            <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="admin-date-filter" className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white/85 outline-none focus:border-gold transition-colors" />
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter" className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white/85 outline-none focus:border-gold transition-colors">
            <option value="all" className="bg-ink">Tutti gli stati</option>
            {tab === "orders" ? (
              <>
                <option value="new" className="bg-ink">Nuovi</option>
                <option value="preparing" className="bg-ink">In preparazione</option>
                <option value="ready" className="bg-ink">Pronti</option>
                <option value="delivered" className="bg-ink">Consegnati</option>
                <option value="cancelled" className="bg-ink">Annullati</option>
              </>
            ) : (
              <>
                <option value="confirmed" className="bg-ink">Confermata</option>
                <option value="pending" className="bg-ink">In attesa</option>
                <option value="cancelled" className="bg-ink">Annullata</option>
              </>
            )}
          </select>
          <button onClick={resetFilters} data-testid="admin-filters-reset" className="text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition-colors">
            <Filter size={13} className="inline mr-1" /> Reset
          </button>
        </div>
        )}

        {/* Content */}
        {tab === "orders" && (
          <OrdersList orders={orders} fetching={fetching} onStatus={updateOStatus} onDelete={removeOrd} actioning={actioning} />
        )}
        {tab === "reservations" && (
          <ReservationsTable list={reservations} fetching={fetching} onStatus={updateRStatus} onDelete={removeRes} actioning={actioning} />
        )}
      </main>
    </div>
  );
}

function QRTablesPanel() {
  const [count, setCount] = useState(10);
  const base = window.location.origin;
  const tables = Array.from({ length: count }, (_, i) => i + 1);
  const qrUrl = (n) => `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&color=111111&bgcolor=ffffff&data=${encodeURIComponent(`${base}/?table=${n}#menu`)}`;
  return (
    <div className="space-y-6" data-testid="admin-qr-panel">
      <div className="rounded-2xl bg-ash/60 border border-white/10 p-5">
        <div className="flex items-center gap-3">
          <label className="text-sm text-white/70">Numero di tavoli:</label>
          <input type="number" min={1} max={100} value={count} onChange={(e) => setCount(Math.max(1, Math.min(100, +e.target.value || 1)))}
            className="w-20 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-gold"
            data-testid="admin-qr-count" />
          <span className="text-xs text-white/50">Stampa un QR per tavolo. Il cliente inquadra e ordina — la modalità <span className="text-gold">Al tavolo</span> e il numero vengono auto-compilati.</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {tables.map((n) => (
          <div key={n} className="rounded-2xl bg-white p-4 flex flex-col items-center gap-2" data-testid={`admin-qr-card-${n}`}>
            <img src={qrUrl(n)} alt={`QR tavolo ${n}`} className="w-full h-auto" loading="lazy" />
            <div className="text-ink font-display font-semibold text-lg">Tavolo {n}</div>
            <a href={qrUrl(n)} download={`drago-tavolo-${n}.png`} className="text-[10px] text-ink/60 hover:text-ember uppercase tracking-widest">Scarica</a>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${active ? "bg-ember text-white" : "text-white/70 hover:text-white"}`}>
      {icon} {children}
    </button>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "bg-ember/10 border-ember/30" : "bg-ash/60 border-white/10"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/50">
        <span className={accent ? "text-gold" : "text-white/50"}>{icon}</span> {label}
      </div>
      <div className="mt-3 font-display text-3xl font-semibold">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, loading, testId }) {
  return (
    <button onClick={onClick} title={title} disabled={loading} data-testid={testId} className="w-8 h-8 rounded-full grid place-items-center bg-black/40 border border-white/10 hover:border-white/30 hover:bg-white/5 disabled:opacity-50 transition-colors">
      {loading ? <Loader2 size={13} className="animate-spin text-white/70" /> : children}
    </button>
  );
}

function safeFormatDate(s) {
  try { return format(parseISO(s), "d MMM yyyy", { locale: it }); }
  catch { return s; }
}

function ReservationsTable({ list, fetching, onStatus, onDelete, actioning }) {
  return (
    <div className="rounded-2xl bg-ash/40 border border-white/10 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-reservations-table">
          <thead className="bg-black/40 text-white/50 text-xs uppercase tracking-[0.14em]">
            <tr>
              <th className="text-left px-5 py-4 font-medium">Data · Ora</th>
              <th className="text-left px-5 py-4 font-medium">Ospite</th>
              <th className="text-left px-5 py-4 font-medium">Contatti</th>
              <th className="text-left px-5 py-4 font-medium">Pax</th>
              <th className="text-left px-5 py-4 font-medium">Note</th>
              <th className="text-left px-5 py-4 font-medium">Stato</th>
              <th className="text-right px-5 py-4 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {fetching && list.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40"><Loader2 className="inline animate-spin mr-2" size={14} /> Carico...</td></tr>
            )}
            {!fetching && list.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-16 text-center text-white/40" data-testid="admin-empty-state">Nessuna prenotazione trovata.</td></tr>
            )}
            {list.map((r) => {
              const meta = R_STATUS[r.status] || R_STATUS.pending;
              return (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-reservation-row-${r.id}`}>
                  <td className="px-5 py-4 whitespace-nowrap">
                    <div className="text-white">{safeFormatDate(r.date)}</div>
                    <div className="text-white/50 text-xs">{r.time}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-white">{r.name} {r.surname}</div>
                    <div className="text-white/40 text-xs font-mono">{r.id.slice(0, 8).toUpperCase()}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 text-white/80 text-xs"><Phone size={11} className="text-white/40" /> {r.phone}</div>
                    <div className="flex items-center gap-1.5 text-white/60 text-xs mt-1"><MailIcon size={11} className="text-white/40" /> {r.email}</div>
                  </td>
                  <td className="px-5 py-4 text-white">{r.people}</td>
                  <td className="px-5 py-4 max-w-[220px]"><div className="text-white/60 text-xs line-clamp-2">{r.notes || "—"}</div></td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${meta.cls}`} data-testid={`admin-status-${r.id}`}>{meta.label}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {r.status !== "confirmed" && <IconBtn testId={`admin-confirm-${r.id}`} title="Conferma" onClick={() => onStatus(r.id, "confirmed")} loading={actioning === r.id + "confirmed"}><CheckCircle2 size={15} className="text-emerald-400" /></IconBtn>}
                      {r.status !== "cancelled" && <IconBtn testId={`admin-cancel-${r.id}`} title="Annulla" onClick={() => onStatus(r.id, "cancelled")} loading={actioning === r.id + "cancelled"}><XCircle size={15} className="text-amber-400" /></IconBtn>}
                      <IconBtn testId={`admin-delete-${r.id}`} title="Elimina" onClick={() => onDelete(r.id)} loading={actioning === r.id + "del"}><Trash2 size={15} className="text-red-400" /></IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersList({ orders, fetching, onStatus, onDelete, actioning }) {
  if (fetching && orders.length === 0) {
    return <div className="rounded-2xl bg-ash/40 border border-white/10 p-10 text-center text-white/40"><Loader2 className="inline animate-spin mr-2" size={14} /> Carico...</div>;
  }
  if (!fetching && orders.length === 0) {
    return <div className="rounded-2xl bg-ash/40 border border-white/10 p-16 text-center text-white/40" data-testid="admin-orders-empty">Nessun ordine trovato.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-orders-list">
      {orders.map((o) => {
        const meta = O_STATUS[o.status] || O_STATUS.new;
        return (
          <div key={o.id} className="rounded-2xl bg-ash/50 border border-white/10 p-5" data-testid={`admin-order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="text-xs text-white/40 uppercase tracking-widest">#{o.id.slice(0, 8).toUpperCase()}</div>
                <div className="mt-1 text-white font-medium">{o.customer_name}</div>
                <div className="text-white/60 text-xs mt-0.5">{o.customer_phone}</div>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${meta.cls}`} data-testid={`admin-order-status-${o.id}`}>{meta.label}</span>
                <div className="mt-2 text-gold font-display text-xl">€{Number(o.total_price).toFixed(Number(o.total_price) % 1 ? 2 : 0)}</div>
              </div>
            </div>
            <div className="text-xs text-white/50 mb-3">
              {o.order_type === "dinein"
                ? (o.table_number ? `Al tavolo n. ${o.table_number}` : "Al tavolo")
                : o.order_type === "delivery"
                ? `Consegna · ${o.address || "—"}`
                : "Asporto"} · {(() => { try { return format(parseISO(o.created_at), "d MMM · HH:mm", { locale: it }); } catch { return o.created_at; } })()}
            </div>
            <div className="border-t border-white/5 pt-3 space-y-1">
              {o.items.map((it, idx) => (
                <div key={`${it.menu_id}-${idx}`} className="flex justify-between text-sm">
                  <span className="text-white/80">{it.qty}× {it.name}{it.option ? ` (${it.option})` : ""}</span>
                  <span className="text-white/50">€{(it.price * it.qty).toFixed((it.price * it.qty) % 1 ? 2 : 0)}</span>
                </div>
              ))}
              {o.notes && <div className="mt-2 pt-2 border-t border-white/5 text-xs text-white/50 italic">Note: {o.notes}</div>}
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <select
                value={o.status}
                onChange={(e) => onStatus(o.id, e.target.value)}
                data-testid={`admin-order-status-select-${o.id}`}
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-3 py-2 text-xs text-white outline-none focus:border-gold"
              >
                <option value="new" className="bg-ink">Nuovo</option>
                <option value="preparing" className="bg-ink">In preparazione</option>
                <option value="ready" className="bg-ink">Pronto</option>
                <option value="delivered" className="bg-ink">Consegnato</option>
                <option value="cancelled" className="bg-ink">Annullato</option>
              </select>
              <IconBtn testId={`admin-order-delete-${o.id}`} title="Elimina" onClick={() => onDelete(o.id)} loading={actioning === o.id + "del"}>
                <Trash2 size={15} className="text-red-400" />
              </IconBtn>
            </div>
          </div>
        );
      })}
    </div>
  );
}
